import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { services } from "@/data/services";

interface FormData {
  name: string;
  phone: string;
  suburb: string;
  service_needed: string;
  property_type: string;
  timeframe: string;
  details: string;
}

const initialForm: FormData = {
  name: "",
  phone: "",
  suburb: "",
  service_needed: "",
  property_type: "",
  timeframe: "",
  details: "",
};

const propertyTypes = ["House", "Apartment / Unit", "Townhouse", "Rental Property", "Shop / Retail", "Office", "Warehouse", "Other"];
const timeframes = ["As soon as possible", "Within 1–2 weeks", "Within a month", "Just planning ahead", "Flexible"];

interface QuoteFormProps {
  showHeading?: boolean;
}

const QuoteForm = ({ showHeading = true }: QuoteFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormData>(initialForm);
  const [photoNames, setPhotoNames] = useState<string[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    else if (!/^[\d\s()+-]{7,20}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!form.suburb.trim()) e.suburb = "Suburb is required";
    if (!form.service_needed) e.service_needed = "Please select a service";
    if (!form.property_type) e.property_type = "Please select a property type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const photoNote = photoNames.length ? `\n\nPhotos to follow: ${photoNames.join(", ")}` : "";
    const { error } = await supabase.from("property_quote_requests").insert({
      name: form.name.trim(),
      phone: form.phone.trim(),
      suburb: form.suburb.trim(),
      service_needed: form.service_needed,
      property_type: form.property_type,
      timeframe: form.timeframe || null,
      details: (form.details.trim() + photoNote).trim() || null,
    });
    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
      return;
    }
    setSubmitted(true);
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3.5 border-2 rounded-md bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/30 focus:border-[#FFC300] focus:outline-none transition-colors ${
      errors[field] ? "border-red-400" : "border-[#1a1a1a]/10"
    }`;

  return (
    <section id="quote" className="bg-[#f7f7f7] py-16 md:py-20">
      <div className="container max-w-2xl">
        {showHeading && (
          <>
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-3">
              Get Your Property Services Quote in 60 Seconds
            </h2>
            <p className="text-center text-[#1a1a1a]/75 mb-6 text-base">
              Send us a few details and we'll get back to you with pricing, availability and next steps.
            </p>
            <p className="text-center text-xs font-semibold text-[#1a1a1a]/70 tracking-wide mb-10">
              Fast response &nbsp;•&nbsp; Clear pricing &nbsp;•&nbsp; Residential &amp; commercial
            </p>
          </>
        )}

        {submitted ? (
          <div className="bg-[#e8f5e9] border-2 border-[#4caf50] rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">
              Thanks — we've received your quote request!
            </h3>
            <p className="text-[#1a1a1a]/60">
              We'll be in touch shortly with pricing, availability and next steps.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  aria-label="Your name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={inputClass("name")}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  aria-label="Phone number"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className={inputClass("phone")}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  placeholder="Suburb"
                  aria-label="Suburb"
                  value={form.suburb}
                  onChange={(e) => handleChange("suburb", e.target.value)}
                  className={inputClass("suburb")}
                />
                {errors.suburb && <p className="text-red-500 text-xs mt-1">{errors.suburb}</p>}
              </div>
              <div>
                <select
                  aria-label="Service needed"
                  value={form.service_needed}
                  onChange={(e) => handleChange("service_needed", e.target.value)}
                  className={`${inputClass("service_needed")} ${form.service_needed ? "" : "text-[#1a1a1a]/30"}`}
                >
                  <option value="">Service Needed</option>
                  {services.map((s) => (
                    <option key={s.slug} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Not sure / Other">Not sure / Other</option>
                </select>
                {errors.service_needed && <p className="text-red-500 text-xs mt-1">{errors.service_needed}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <select
                  aria-label="Property type"
                  value={form.property_type}
                  onChange={(e) => handleChange("property_type", e.target.value)}
                  className={`${inputClass("property_type")} ${form.property_type ? "" : "text-[#1a1a1a]/30"}`}
                >
                  <option value="">Property Type</option>
                  {propertyTypes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {errors.property_type && <p className="text-red-500 text-xs mt-1">{errors.property_type}</p>}
              </div>
              <div>
                <select
                  aria-label="Preferred timeframe"
                  value={form.timeframe}
                  onChange={(e) => handleChange("timeframe", e.target.value)}
                  className={`${inputClass("timeframe")} ${form.timeframe ? "" : "text-[#1a1a1a]/30"}`}
                >
                  <option value="">Preferred Timeframe</option>
                  {timeframes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1a1a1a]/70 mb-1.5">
                Upload Photos (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                aria-label="Upload photos of the job"
                onChange={(e) => setPhotoNames(Array.from(e.target.files ?? []).map((f) => f.name))}
                className="w-full text-sm text-[#1a1a1a]/70 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:bg-[#1a1a1a] file:text-[#FFC300] file:font-bold file:cursor-pointer"
              />
              {photoNames.length > 0 && (
                <p className="text-xs text-[#1a1a1a]/60 mt-1.5">{photoNames.length} photo(s) selected — we'll request these after you submit.</p>
              )}
            </div>

            <textarea
              placeholder="Tell us about the job"
              aria-label="Tell us about the job"
              rows={3}
              value={form.details}
              onChange={(e) => handleChange("details", e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-[#1a1a1a]/10 rounded-md bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/30 focus:border-[#FFC300] focus:outline-none transition-colors resize-none"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-[#FFC300] py-4 text-lg font-bold rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {loading ? "Submitting…" : "Get My Free Quote"}
            </button>
            <p className="text-center text-xs text-[#1a1a1a]/70">
              No obligation. We'll respond quickly.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default QuoteForm;
